import LZString from "lz-string";
type StorageStrategy = "lz-string" | "uncompressed";

export class DraftStorage {
  private readonly DRAFT_KEY: string;
  private readonly DRAFT_TIMESTAMP_KEY: string;
  private readonly storageStrategy: StorageStrategy;
  private readonly worker: Worker | null;

  constructor({
    draftKey,
    draftTimestampKey,
    storageStrategy = "lz-string",
    worker = typeof Worker !== "undefined"
      ? new Worker("/compress-worker.js")
      : null,
  }: {
    draftKey: string;
    draftTimestampKey: string;
    storageStrategy?: StorageStrategy;
    worker?: Worker | null;
  }) {
    this.DRAFT_KEY = draftKey;
    this.DRAFT_TIMESTAMP_KEY = draftTimestampKey;
    this.storageStrategy = storageStrategy;
    this.worker = worker;
  }

  public get(): string | null {
    try {
      const stored = localStorage.getItem(this.DRAFT_KEY);
      if (stored) {
        if (this.storageStrategy === "uncompressed") {
          return stored;
        } else if (this.storageStrategy === "lz-string") {
          const result = LZString.decompressFromUTF16(stored);
          if (result === null) {
            console.warn(
              "[Draft] Decompression failed, clearing stored draft."
            );
            this.clear();
            return null;
          }
          return result;
        }
      }
      return null;
    } catch (e) {
      console.warn("[Draft] Failed to read from localStorage", e);
      return null;
    }
  }

  private save(content: string): boolean {
    try {
      let compressedContent = content;
      if (this.storageStrategy === "lz-string") {
        compressedContent = LZString.compressToUTF16(content);
      }
      localStorage.setItem(this.DRAFT_KEY, compressedContent);
      localStorage.setItem(this.DRAFT_TIMESTAMP_KEY, Date.now().toString());
      return true;
    } catch (e) {
      console.warn("[Draft] Failed to save to localStorage", e);
      return false;
    }
  }

  public async saveAsync(content: string): Promise<boolean> {
    if (this.storageStrategy === "lz-string" && this.worker) {
      return new Promise((resolve) => {
        let timeoutId: number;

        const handleWorkerMessage = (e: MessageEvent) => {
          const { compressed } = e.data;

          clearTimeout(timeoutId);

          if (!compressed) {
            console.warn(
              "[Draft] Compression failed or returned empty string."
            );
            resolve(false);
            return;
          }

          try {
            localStorage.setItem(this.DRAFT_KEY, compressed);
            localStorage.setItem(
              this.DRAFT_TIMESTAMP_KEY,
              Date.now().toString()
            );
            resolve(true);
          } catch (e) {
            console.warn("[Draft] Failed to save to localStorage", e);
            resolve(false);
          } finally {
            this.worker?.removeEventListener("message", handleWorkerMessage);
          }
        };

        this.worker?.addEventListener("message", handleWorkerMessage, {
          once: true,
        });
        this.worker?.postMessage(content);

        //Set a timeout to handle cases where the worker does not respond in time
        timeoutId = setTimeout(() => {
          this.worker?.removeEventListener("message", handleWorkerMessage);
          console.warn("[Draft] Worker response timeout.");
          resolve(false);
        }, 5000); // Timeout after 5 seconds

        // Handle worker error
        this.worker?.addEventListener(
          "error",
          (e) => {
            clearTimeout(timeoutId);
            console.warn("[Draft] Worker error: ", e);
            resolve(false);
          },
          { once: true }
        );
      });
    } else {
      return Promise.resolve(this.save(content));
    }
  }

  public clear() {
    try {
      localStorage.removeItem(this.DRAFT_KEY);
      localStorage.removeItem(this.DRAFT_TIMESTAMP_KEY);
    } catch (e) {
      console.warn("[Draft] Failed to clear localStorage", e);
    }
  }
}
